import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDown, Filter } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import type { UserRole } from '@/types';

interface UserFiltersProps {
  selected: string[];
  onSelect: (roles: string[]) => void;
  title?: string;
}

export function UserFilters({ selected, onSelect, title }: UserFiltersProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { t, dir } = useLanguage();
  const roles: UserRole[] = ['admin', 'coach', 'customer', 'examiner', 'manager', 'partner', 'volunteer', 'instructor', 'organizer', 'provider', 'coordinator', 'marketer', 'audit'];

  const toggleRole = (role: string) => {
    if (selected.includes(role)) {
      onSelect(selected.filter(r => r !== role));
    } else {
      onSelect([...selected, role]);
    }
  };

  const clearAll = () => {
    onSelect([]);
  };

  const selectAll = () => {
    onSelect(roles);
  };

  return (
    <div className="flex flex-col gap-2">
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className={`flex items-center gap-2 ${dir === 'rtl' ? 'flex-row-reverse' : 'flex-row'}`}>
            <Filter className="h-4 w-4" />
            {selected.length === 0 ? (
              t('dashboard.admin.users.filters.allRoles')
            ) : selected.length === 1 ? (
              t(`dashboard.admin.users.roles.${selected[0]}`)
            ) : (
              `${selected.length} ${t('dashboard.admin.users.filters.selected')}`
            )}
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        
        <DropdownMenuContent className="w-56" align={dir === 'rtl' ? 'end' : 'start'}>
          <DropdownMenuLabel>{title || t('dashboard.admin.users.filters.selectRoles')}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          <DropdownMenuCheckboxItem
            checked={selected.length === roles.length}
            onCheckedChange={selectAll}
          >
            {t('dashboard.admin.users.filters.selectAll')}
          </DropdownMenuCheckboxItem>
          
          <DropdownMenuCheckboxItem
            checked={selected.length === 0}
            onCheckedChange={clearAll}
          >
            {t('dashboard.admin.users.filters.clearAll')}
          </DropdownMenuCheckboxItem>
          
          <DropdownMenuSeparator />
          
          {roles.map((role) => (
            <DropdownMenuCheckboxItem
              key={role}
              checked={selected.includes(role)}
              onCheckedChange={() => toggleRole(role)}
            >
              {t(`dashboard.admin.users.roles.${role}`)}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>


    </div>
  );
}